import type { CSSProperties, ReactNode } from 'react';

export function Card({
  children,
  style,
  padded = true,
}: {
  children: ReactNode;
  style?: CSSProperties;
  padded?: boolean;
}) {
  return (
    <div
      style={{
        background: 'var(--card-bg)',
        border: '1px solid var(--border-soft)',
        boxShadow: 'var(--card-stack)',
        borderRadius: 'var(--radius-lg)',
        padding: padded ? '20px 22px' : 0,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/* Eyebrow — three-tier size system.

   • sm — Sans 11 / 600 / 0.06em · tight chips, list rows, inline metadata
   • md — Sans 13 / 600 / 0.10em · DEFAULT · card headers, section labels
   • lg — Sans Expanded (wdth 125%) 18 / 300 / 0.02em · one-per-page
          featured callout (retains the former `StrongEyebrow` role)

   Rules carried in from earlier type explorations:
   - Expanded pairs with light weights only; never Bold + Expanded.
   - `lg` should appear at most once per page; overuse dilutes it.

   Callers not passing `size` land on `md` — the new baseline. */
export function Eyebrow({
  children,
  tone = 'light',
  size = 'md',
}: {
  children: ReactNode;
  tone?: 'light' | 'dark' | 'denim';
  size?: 'sm' | 'md' | 'lg';
}) {
  const color =
    tone === 'dark'
      ? 'rgba(255,255,255,0.72)'
      : tone === 'denim'
      ? 'var(--denim-fg)'
      : 'var(--fg-3)';

  if (size === 'lg') {
    return (
      <span
        className="mo-expanded"
        style={{
          fontWeight: 300,
          fontSize: 18,
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          color,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        {children}
      </span>
    );
  }

  const spec =
    size === 'sm'
      ? { fontSize: 11, letterSpacing: '0.06em' }
      : { fontSize: 13, letterSpacing: '0.1em' };

  return (
    <span
      style={{
        fontFamily: 'var(--font-sans)',
        fontWeight: 600,
        ...spec,
        textTransform: 'uppercase',
        color,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      {children}
    </span>
  );
}

/* GameKicker — the "// GAME NAME" mono coral eyebrow shared by the daily-game
   cards (homepage hero + /games), so every game reads with one kicker style.
   coral-deep on light surfaces, the peachy --coral on dark. Distinct from the
   muted design-system Eyebrow above. */
export function GameKicker({
  children,
  onDark = false,
}: {
  children: ReactNode;
  onDark?: boolean;
}) {
  return (
    <span
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: onDark ? 'var(--coral)' : 'var(--coral-deep)',
      }}
    >
      {children}
    </span>
  );
}

export function DenimButton({
  children,
  size = 'md',
  style,
}: {
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  style?: CSSProperties;
}) {
  const pad = size === 'sm' ? '7px 14px' : size === 'lg' ? '14px 24px' : '10px 18px';
  const fs = size === 'sm' ? 12 : size === 'lg' ? 15 : 13;
  return (
    <button
      style={{
        background: 'var(--denim)',
        color: '#fff',
        border: 0,
        borderRadius: 8,
        padding: pad,
        fontFamily: 'var(--font-sans)',
        fontWeight: 600,
        fontSize: fs,
        cursor: 'pointer',
        boxShadow: 'var(--shadow-sm)',
        transition: 'background .15s, box-shadow .15s',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

export function AccentButton({
  children,
  size = 'md',
  style,
}: {
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  style?: CSSProperties;
}) {
  const pad = size === 'sm' ? '7px 14px' : size === 'lg' ? '14px 24px' : '10px 18px';
  const fs = size === 'sm' ? 12 : size === 'lg' ? 15 : 13;
  return (
    <button
      style={{
        background: 'var(--acc)',
        color: 'var(--acc-ink-on-acc)',
        border: 0,
        borderRadius: 999,
        padding: pad,
        fontFamily: 'var(--font-sans)',
        fontWeight: 600,
        fontSize: fs,
        cursor: 'pointer',
        boxShadow: 'var(--shadow-sm)',
        transition: 'background .15s, box-shadow .15s',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

export function GhostButton({
  children,
  active,
  style,
}: {
  children: ReactNode;
  active?: boolean;
  style?: CSSProperties;
}) {
  return (
    <button
      style={{
        background: active ? 'var(--fg-1)' : '#fff',
        color: active ? '#fff' : 'var(--fg-2)',
        border: active ? '1px solid transparent' : '1px solid var(--border)',
        borderRadius: 8,
        padding: '8px 14px',
        fontFamily: 'var(--font-sans)',
        fontWeight: 500,
        fontSize: 12,
        cursor: 'pointer',
        boxShadow: active ? 'none' : 'var(--card-stack)',
        ...style,
      }}
    >
      {children}
    </button>
  );
}

export function Chip({
  children,
  tone = 'slate',
}: {
  children: ReactNode;
  tone?: 'slate' | 'dark' | 'denim' | 'apex' | 'outline';
}) {
  // All tones use flipping tokens so chips adapt to theme. Locked-light
  // brand combos (denim) stay locked because the bg+ink contrast holds
  // on either page-bg.
  const style: CSSProperties =
    tone === 'dark'
      ? { background: 'var(--fg-1)', color: 'var(--fg-inverse)' }
      : tone === 'denim'
      ? { background: 'var(--denim-soft)', color: 'var(--denim-deep)' }
      : tone === 'apex'
      ? { background: 'var(--acc-soft)', color: 'var(--acc-deep)' }
      : tone === 'outline'
      ? { background: 'var(--card-bg)', color: 'var(--fg-3)', border: '1px solid var(--border)' }
      : { background: 'var(--card-bg-soft)', color: 'var(--fg-2)' };

  return (
    <span
      style={{
        fontFamily: 'var(--font-sans)',
        fontSize: 11,
        fontWeight: 500,
        padding: '3px 10px',
        borderRadius: 999,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        ...style,
      }}
    >
      {children}
    </span>
  );
}

export function HotPill({ children }: { children: ReactNode }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        padding: '3px 10px',
        borderRadius: 999,
        fontFamily: 'var(--font-sans)',
        fontSize: 11,
        fontWeight: 500,
        background: 'var(--acc-soft)',
        color: 'var(--acc-deep)',
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: 'var(--acc-deep)',
          boxShadow: '0 0 0 2px var(--acc)',
        }}
      />
      {children}
    </span>
  );
}

export function DisplayNum({ children, size = 56 }: { children: ReactNode; size?: number }) {
  return (
    <span
      style={{
        fontFamily: 'var(--font-display)',
        fontWeight: 200,
        fontSize: size,
        letterSpacing: '-0.015em',
        lineHeight: 1,
        color: 'var(--fg-1)',
        fontVariationSettings: '"wght" 200',
        display: 'inline-block',
      }}
    >
      {children}
    </span>
  );
}

export function ScoreSquares({ values }: { values: Array<'apex' | 'denim' | 'off'> }) {
  return (
    <span style={{ display: 'inline-flex', gap: 4 }}>
      {values.map((v, i) => (
        <span
          key={i}
          style={{
            width: 16,
            height: 16,
            borderRadius: 3,
            background:
              v === 'apex' ? 'var(--acc)' : v === 'denim' ? 'var(--denim)' : 'var(--fg-1)',
          }}
        />
      ))}
    </span>
  );
}

export function FilterPill({
  label,
  selected = false,
  count,
  leadingSwatch,
  onClick,
  size = 'md',
  style,
}: {
  label: string;
  selected?: boolean;
  count?: number;
  leadingSwatch?: string;
  onClick?: () => void;
  size?: 'sm' | 'md';
  style?: CSSProperties;
}) {
  const pad = size === 'sm' ? '4px 10px' : '6px 12px';
  const fs = size === 'sm' ? 11 : 12.5;
  const ring = selected
    ? '0 0 0 2px var(--coral-deep), var(--shadow-sm)'
    : 'var(--card-stack)';
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      aria-label={label}
      title={label}
      className="mo-fp"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: pad,
        borderRadius: 999,
        background: selected ? 'var(--denim-ink)' : 'var(--card-bg)',
        color: selected ? 'var(--fg-inverse)' : 'var(--fg-2)',
        border: selected ? '1px solid transparent' : '1px solid var(--border)',
        fontFamily: 'var(--font-sans)',
        fontWeight: 500,
        fontSize: fs,
        letterSpacing: '-0.005em',
        boxShadow: ring,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'background .15s, box-shadow .15s, color .15s',
        ...style,
      }}
    >
      {leadingSwatch !== undefined && (
        <span
          aria-hidden
          className="mo-fp-swatch"
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: leadingSwatch,
            boxShadow: '0 0 0 1px var(--border-hair)',
            flexShrink: 0,
          }}
        />
      )}
      <span className="mo-fp-label">{label}</span>
      {count !== undefined && (
        <span
          className="mo-fp-count"
          style={{
            fontFamily: 'var(--font-mono)',
            fontVariantNumeric: 'tabular-nums',
            fontSize: fs - 1,
            fontWeight: 500,
            padding: '1px 6px',
            borderRadius: 999,
            background: 'var(--coral-deep)',
            color: '#fff',
            minWidth: 18,
            textAlign: 'center',
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

export function Select({
  value,
  options,
  onChange,
  placeholder,
  size = 'md',
  ariaLabel,
  style,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  placeholder?: string;
  size?: 'sm' | 'md';
  ariaLabel?: string;
  style?: CSSProperties;
}) {
  const h = size === 'sm' ? 28 : 34;
  const fs = size === 'sm' ? 12 : 13;
  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-block',
        ...style,
      }}
    >
      <select
        aria-label={ariaLabel}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          appearance: 'none',
          WebkitAppearance: 'none',
          MozAppearance: 'none',
          height: h,
          padding: '0 32px 0 12px',
          borderRadius: 8,
          border: '1px solid var(--border)',
          background: 'var(--card-bg)',
          color: 'var(--fg-1)',
          fontFamily: 'var(--font-sans)',
          fontWeight: 500,
          fontSize: fs,
          lineHeight: 1,
          boxShadow: 'var(--card-stack)',
          cursor: 'pointer',
          outline: 'none',
        }}
        onFocus={(e) => {
          e.currentTarget.style.boxShadow =
            '0 0 0 2px rgba(44,105,236,0.3), var(--card-stack)';
          e.currentTarget.style.borderColor = 'var(--denim)';
        }}
        onBlur={(e) => {
          e.currentTarget.style.boxShadow = 'var(--card-stack)';
          e.currentTarget.style.borderColor = 'var(--border)';
        }}
      >
        {placeholder !== undefined && !options.find((o) => o.value === value) && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      <span
        aria-hidden
        style={{
          position: 'absolute',
          right: 10,
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          color: 'var(--fg-3)',
          fontSize: 10,
          lineHeight: 1,
        }}
      >
        ▾
      </span>
    </div>
  );
}

export type TabSpec = {
  id: string;
  label: string;
  content: ReactNode;
  disabled?: boolean;
  badge?: number;
};

export function TabBar({
  tabs,
  activeId,
  onChange,
  fill = 'paper',
  style,
}: {
  tabs: TabSpec[];
  activeId: string;
  onChange: (id: string) => void;
  fill?: 'denim' | 'paper';
  style?: CSSProperties;
}) {
  const barBg = fill === 'denim' ? 'var(--denim-ink)' : 'var(--card-bg-soft)';
  const inactiveFg = fill === 'denim' ? 'rgba(255,255,255,0.72)' : 'var(--fg-3)';
  const active = tabs.find((t) => t.id === activeId);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        flex: '1 1 auto',
        ...style,
      }}
    >
      <div
        role="tablist"
        style={{
          display: 'flex',
          gap: 2,
          padding: 4,
          background: barBg,
          borderRadius: 8,
          flexShrink: 0,
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {tabs.map((t) => {
          const isActive = t.id === activeId;
          return (
            <button
              key={t.id}
              role="tab"
              aria-selected={isActive}
              aria-controls={`tabpanel-${t.id}`}
              disabled={t.disabled}
              onClick={() => !t.disabled && onChange(t.id)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                padding: '7px 12px',
                border: 0,
                borderRadius: 6,
                background: isActive
                  ? fill === 'denim'
                    ? 'var(--denim)'
                    : 'var(--coral-deep)'
                  : 'transparent',
                color: isActive
                  ? fill === 'denim'
                    ? 'var(--fg-inverse)'
                    : '#fff'
                  : inactiveFg,
                fontFamily: 'var(--font-sans)',
                fontWeight: 500,
                fontSize: 12,
                cursor: t.disabled ? 'not-allowed' : 'pointer',
                opacity: t.disabled ? 0.5 : 1,
                whiteSpace: 'nowrap',
                boxShadow: isActive && fill === 'paper' ? 'var(--shadow-sm)' : 'none',
                transition: 'background .15s, color .15s',
              }}
            >
              <span>{t.label}</span>
              {t.badge !== undefined && (
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: 10.5,
                    padding: '0 5px',
                    borderRadius: 999,
                    background: isActive ? 'var(--coral-deep)' : 'var(--denim-200)',
                    color: isActive ? '#fff' : 'var(--denim-ink)',
                    minWidth: 16,
                    textAlign: 'center',
                  }}
                >
                  {t.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
      <div
        role="tabpanel"
        id={active ? `tabpanel-${active.id}` : undefined}
        style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', paddingTop: 10 }}
      >
        {active?.content}
      </div>
    </div>
  );
}

export function EmptyState({
  title,
  hint,
  icon,
  size = 'md',
  style,
}: {
  title: string;
  hint?: string;
  icon?: ReactNode;
  size?: 'sm' | 'md';
  style?: CSSProperties;
}) {
  const pad = size === 'sm' ? '18px 16px' : '28px 22px';
  const titleSize = size === 'sm' ? 13 : 15;
  return (
    <div
      role="status"
      style={{
        background: 'var(--card-bg-soft)',
        border: '1px solid var(--border-soft)',
        borderRadius: 'var(--radius-lg)',
        padding: pad,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 8,
        textAlign: 'center',
        color: 'var(--fg-3)',
        ...style,
      }}
    >
      {icon && (
        <span aria-hidden style={{ color: 'var(--fg-4)', display: 'inline-flex' }}>
          {icon}
        </span>
      )}
      <span
        style={{
          fontFamily: 'var(--font-sans)',
          fontWeight: 500,
          fontSize: titleSize,
          color: 'var(--fg-1)',
        }}
      >
        {title}
      </span>
      {hint && (
        <span
          style={{
            fontFamily: 'var(--font-sans)',
            fontWeight: 300,
            fontSize: 12,
            lineHeight: 1.5,
            maxWidth: '32ch',
          }}
        >
          {hint}
        </span>
      )}
    </div>
  );
}
